import { AsyncSubject, Observable } from 'rxjs';
import { Auth } from '@aws-amplify/auth';

import httpService from './httpService';

const { __environment__ } = window as any;

const region = __environment__.REACT_APP_AWS_USER_POOL_REGION;
const userPool = __environment__.REACT_APP_AWS_USER_POOL_ID;
const clientId = __environment__.REACT_APP_AWS_CLIENT_ID;
const domain = __environment__.REACT_APP_AWS_DOMAIN;
const customProvider = __environment__.REACT_APP_AWS_CUSTOM_PROVIDER;
const molinaAPIUri = __environment__.REACT_APP_MOLINA_API_URL;
const userAPIUri = __environment__.REACT_APP_USER_API_URL;
const signinUri = __environment__.REACT_APP_AWS_SIGNIN_URI;
const signoutUri = __environment__.REACT_APP_AWS_SIGNOUT_URI;

export interface Tokens {
  accessToken?: string;
  idToken?: string;
}

class AuthService {
  private newPasswordInfo: any = null;

  constructor() {
    Auth.configure({
      region: region,
      userPoolId: userPool,
      userPoolWebClientId: clientId,
      oauth: {
        domain,
        scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: signinUri,
        redirectSignOut: signoutUri,
        returnTo: signoutUri,
        redirectUri: signoutUri,
        responseType: 'code',
      },
    });
  }

  public getUserInfo(): Observable<{
    name: string;
    email: string;
  }> {
    const subject = new AsyncSubject<{
      name: string;
      email: string;
    }>();
    Auth.currentAuthenticatedUser().then((userInfo) => {
      subject.next({
        name: userInfo.attributes.email,
        email: userInfo.attributes.email,
      });
      subject.complete();
    });
    return subject;
  }

  public getTokens(): Observable<Tokens> {
    const subject = new AsyncSubject<Tokens>();
    Auth.currentSession()
      .then((session) => {
        subject.next({
          accessToken: session?.getAccessToken()?.getJwtToken(),
          idToken: session?.getIdToken()?.getJwtToken(),
        });
        subject.complete();
      })
      .catch((err) => {
        subject.next({});
        subject.complete();
      });
    return subject;
  }

  public login(username: string, password: string): Promise<Tokens | null> {
    return new Promise((resolve, reject) => {
      try {
        authService.emailCamel(username).subscribe((value) => {
          // @ts-ignore
          const camelUsername = value.email;
          Auth.configure({ authenticationFlowType: 'USER_PASSWORD_AUTH' });
          Auth.signIn(camelUsername, password)
            .then((value) => {
              if (value.challengeName === 'NEW_PASSWORD_REQUIRED') {
                this.newPasswordInfo = value;
                resolve(null);
              } else {
                const { accessToken, idToken } = value.signInUserSession;
                resolve({
                  accessToken: accessToken.jwtToken,
                  idToken: idToken.jwtToken,
                });
              }
            })
            .catch((err) => {
              reject('Loggin failed');
            });
        });
      } catch (error) {
        reject('Loggin failed');
      }
    });
  }

  public async signup(username: string, password: string) {
    return new Promise((resolve, reject) => {
      Auth.signUp({
        username,
        password,
        attributes: {
          email: username, // optional
          // other custom attributes
        },
        autoSignIn: {
          // optional - enables auto sign in after user is confirmed
          enabled: true,
        },
      })
        .then((user) => resolve(user))
        .catch((err) => reject(err.message));
    });
  }

  public loginFederated(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.federatedSignIn({
        customProvider,
      })
        .then((value) => {
          resolve();
        })
        .catch((err) => {
          reject('Loggin failed');
        });
    });
  }

  public async handleLoginCallback(url: string): Promise<Tokens | null> {
    return new Promise(async (resolve) => {
      Auth.currentSession()
        .then((session) => {
          resolve({
            accessToken: session?.getAccessToken()?.getJwtToken(),
            idToken: session?.getIdToken()?.getJwtToken(),
          });
        })
        .finally(() => {
          resolve(null);
        });
    });
  }

  public logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.signOut({ global: true })
        .then(() => {
          resolve();
        })
        .catch((err) => {
          reject('Logout failed');
        });
    });
  }

  public emailCamel(email: string): Observable<string> {
    const url = `${molinaAPIUri}/confirmSignUp`;
    return httpService.postJSON(url, { email: email, findUsername: true });
  }

  public forgotPassword(email: string): Observable<void> {
    const url = `${molinaAPIUri}/resetCognitoPassword`;
    return httpService.postJSON(url, { email });
  }

  public confirmSignUp(email: string): Observable<void> {
    const url = `${molinaAPIUri}/confirmSignUp`;
    return httpService.postJSON(url, { email });
  }

  public getCurrentUser(): Observable<void> {
    const url = `${userAPIUri}/partners/molina/users/me`;
    return httpService.getJSON(url);
  }

  public activateAccount(password: string): Promise<Tokens | null> {
    return new Promise((resolve, reject) => {
      Auth.completeNewPassword(this.newPasswordInfo, password)
        .then((value) => {
          const { accessToken, idToken } = value.signInUserSession;
          resolve({
            accessToken: accessToken.jwtToken,
            idToken: idToken.jwtToken,
          });
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          this.newPasswordInfo = null;
        });
    });
  }
}

const authService = new AuthService();

export default authService;
